import type { ReactNode, VFC } from 'react';

import { NO_SAFE_VERSION, UNABLE_TO_CALCULATE } from '../../../../../shared/vulns/remediation';
import { UpgradeDistance } from '../../../../../shared/vulns/types';
import { Badge } from '../../../generic/Badge';
import Flex from '../../../generic/Flex';
import { Icon, icons } from '../../../generic/Icon';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import Tooltip, { TooltipContent } from '../../../generic/Tooltip';

const UPGRADE_DISTANCE_LABELS: Record<UpgradeDistance, string> = {
  [UpgradeDistance.MAJOR]: 'Major fix',
  [UpgradeDistance.MINOR]: 'Minor fix',
  [UpgradeDistance.PATCH]: 'Patch fix',
};

const UPGRADE_DISTANCE_ICONS: Record<UpgradeDistance, ReactNode> = {
  [UpgradeDistance.MAJOR]: <Icon icon={icons.majorFix} size={ICON_SIZE.LG} />,
  [UpgradeDistance.MINOR]: <Icon icon={icons.minorFix} size={ICON_SIZE.LG} />,
  [UpgradeDistance.PATCH]: <Icon icon={icons.patchFix} size={ICON_SIZE.LG} />,
};

function getFixMetadata(
  version: string,
  distance?: UpgradeDistance,
): { label: string; icon?: ReactNode; tooltip: string } {
  if (version === NO_SAFE_VERSION) {
    return { label: 'No fix', icon: <Icon icon={icons.noFix} size={ICON_SIZE.LG} />, tooltip: 'Fix unavailable' };
  }
  if (version === UNABLE_TO_CALCULATE) {
    return { label: 'Unknown fix', tooltip: 'The upgrade distance could not be calculated' };
  }
  if (!distance) {
    return {
      label: 'Has fix',
      icon: <Icon icon={icons.hasFix} size={ICON_SIZE.LG} />,
      tooltip: 'A fix is available, however the upgrade distance could not be calculated.',
    };
  }

  return {
    label: UPGRADE_DISTANCE_LABELS[distance],
    icon: UPGRADE_DISTANCE_ICONS[distance],
    tooltip: 'Upgrade distance to fix vulnerability',
  };
}

type Props = {
  version: string;
  distance?: UpgradeDistance;
  showTooltip?: boolean;
};

/**
 * Display a simple badge with the given next safe version and distance.
 */
const FixBadge: VFC<Props> = ({ version, distance, showTooltip = true }) => {
  const { label, icon, tooltip } = getFixMetadata(version, distance);

  return (
    <Tooltip id="fix-badge" tooltip={<TooltipContent maxWidth="280px">{tooltip}</TooltipContent>} showTooltip={showTooltip}>
      <div>
        <Badge color="white">
          <Flex gap="4px">
            {icon}
            {label}
          </Flex>
        </Badge>
      </div>
    </Tooltip>
  );
};

export default FixBadge;
